import { apiClient } from "./authService";
import type { PlatformKey } from "../platform/config";

export type SubscriptionStatus =
  | "UNLIMITED"
  | "TRIAL"
  | "ACTIVE"
  | "EXPIRING"
  /** Past the end date; still open for a few days while the owner pays. */
  | "GRACE"
  | "EXPIRED";

export interface SubscriptionPackage {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  durationDays: number;
  platform: PlatformKey | null;
  features: string[];
  isActive: boolean;
  sortOrder: number;
}

export interface MySubscription {
  platform: PlatformKey;
  status: SubscriptionStatus;
  /** null when unlimited; 0 once expired. */
  daysLeft: number | null;
  endsAt: string | null;
  isTrial: boolean;
  plan: SubscriptionPackage | null;
  organizationCreatedAt: string;
}

export interface MyPackages {
  organizationName: string;
  packages: SubscriptionPackage[];
  /** Digits with country code; null until the platform sets one. */
  contactWhatsapp: string | null;
  /** "demo" until the Whish credentials are set on the server. */
  whishMode: "live" | "demo";
}

/** One Whish payment for a package; also the owner's invoice. */
export interface SubscriptionPayment {
  id: string;
  invoiceNumber: number;
  planName: string;
  durationDays: number;
  amount: number;
  currency: string;
  status: "PENDING" | "PAID" | "FAILED";
  periodStartsAt: string | null;
  periodEndsAt: string | null;
  paidAt: string | null;
  failureReason: string | null;
  createdAt: string;
}

/** The owner is asked to pay this many days before the end date. */
export const PAYMENT_REMINDER_DAYS = 3;

/** Both calls stay reachable after the subscription ends. */
export const getMySubscription = async (): Promise<MySubscription> => {
  const { data } = await apiClient.get<MySubscription>("/subscription", {
    // A background check; the banner and the lock screen speak for it.
    suppressToast: true,
  } as any);
  return data;
};

export const getMyPackages = async (): Promise<MyPackages> => {
  const { data } = await apiClient.get<{
    organizationName: string;
    plans: SubscriptionPackage[];
    contactWhatsapp: string | null;
    onlinePayment?: { provider: string; mode: "live" | "demo" };
  }>("/subscription/plans");
  return {
    organizationName: data.organizationName,
    packages: Array.isArray(data.plans) ? data.plans : [],
    contactWhatsapp: data.contactWhatsapp,
    whishMode: data.onlinePayment?.mode ?? "demo",
  };
};

/** A wa.me link with the organization and package already typed. */
export const contactLink = (
  number: string,
  organizationName: string,
  pkg?: SubscriptionPackage
) => {
  const text = pkg
    ? `Hello, I'd like to subscribe "${organizationName}" to the ${pkg.name} package (${pkg.price} ${pkg.currency}).`
    : `Hello, I'd like to renew the subscription for "${organizationName}".`;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
};

/**
 * Starts a Whish payment for a package. The owner then pays on `collectUrl`
 * (opened in a popup): enters their Whish number and confirms with the OTP in
 * the Whish app; Whish sends that window to `returnUrl?payment=<id>`.
 */
export const startWhishPayment = async (
  planId: string,
  returnUrl: string
): Promise<{
  payment: SubscriptionPayment;
  /** Whish's payment page; null in demo mode, where the window simulates it. */
  collectUrl: string | null;
  mode: "live" | "demo";
}> => {
  const { data } = await apiClient.post("/subscription/payments", { planId, returnUrl });
  return data as any;
};

export const getPayment = async (id: string): Promise<SubscriptionPayment> => {
  const { data } = await apiClient.get<SubscriptionPayment>(`/subscription/payments/${id}`, {
    suppressToast: true,
  } as any);
  return data;
};

export const listMyPayments = async (): Promise<SubscriptionPayment[]> => {
  const { data } = await apiClient.get<{ payments: SubscriptionPayment[] }>(
    "/subscription/payments"
  );
  return Array.isArray(data?.payments) ? data.payments : [];
};

/** The owner closed the payment window before paying: marks it failed. */
export const cancelPayment = async (id: string): Promise<SubscriptionPayment> => {
  const { data } = await apiClient.post<SubscriptionPayment>(
    `/subscription/payments/${id}/cancel`,
    undefined,
    { suppressToast: true } as any
  );
  return data;
};

/** Demo mode only: the payment window reports what the payer did. */
export const confirmDemoPayment = async (
  id: string,
  outcome: "success" | "failed"
): Promise<SubscriptionPayment> => {
  const { data } = await apiClient.post<SubscriptionPayment>(
    `/subscription/payments/${id}/demo-confirm`,
    { outcome }
  );
  return data;
};

/** "29 USD", "1,500,000 LBP". */
export const formatMoney = (amount: number, currency: string) =>
  `${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${currency}`;
